import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Invoice, type Booking, type InvoiceStatus, type Package } from "../lib/api";
import { Modal } from "../components/ui/Modal";
import { StatusBadge } from "../components/StatusBadge";
import { formatPrice } from "../lib/format";
import { navigate } from "../lib/route";

const INVOICE_STATUS_COLORS: Record<InvoiceStatus, string> = {
  draft: "#6b7280",
  sent:  "#3b82f6",
  paid:  "#22c55e",
  void:  "#ef4444",
};

export function InvoicesPage() {
  const { t } = useTranslation();
  const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
    draft: t("invoices.status.draft"),
    sent:  t("invoices.status.sent"),
    paid:  t("invoices.status.paid"),
    void:  t("invoices.status.void"),
  };
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [_packages, _setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create flow
  const [creating, setCreating] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedBookingId, setSelectedBookingId] = useState("");
  const [creating2, setCreating2] = useState(false);

  // Action feedback
  const [actionError, setActionError] = useState("");

  async function load() {
    try {
      const [invs, pkgs] = await Promise.all([api.listInvoices(), api.listPackages().catch(() => [] as Package[])]);
      setInvoices(invs ?? []);
      _setPackages(pkgs ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function openCreate() {
    setCreating(true);
    setSelectedBookingId("");
    setActionError("");
    try {
      const bks = await api.listBookings("completed");
      setBookings(bks ?? []);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : "Failed to load bookings");
    }
  }

  async function handleCreate() {
    if (!selectedBookingId) return;
    setCreating2(true);
    setActionError("");
    try {
      const inv = await api.createInvoiceFromBooking(selectedBookingId);
      setInvoices((prev) => [inv, ...prev]);
      setCreating(false);
      navigate('/app/invoices/' + inv.ID);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : "Failed to create invoice");
    } finally {
      setCreating2(false);
    }
  }

  if (loading) return <div className="page-container" aria-busy="true" />;
  if (error) return <div className="page-container"><p style={{ color: "#ef4444" }}>{error}</p></div>;

  return (
    <div className="page-container">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <h1 style={{ margin: 0 }}>{t("invoices.title")}</h1>
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
          <button
            className="btn-ghost"
            style={{ fontSize: "0.875rem" }}
            onClick={() => navigate('/app/invoice-settings')}
          >
            Factuurinstellingen →
          </button>
          <button className="btn-primary" onClick={openCreate}>
            {t("invoices.new")}
          </button>
        </div>
      </div>

      {actionError && (
        <p style={{ color: "#ef4444", marginBottom: "1rem" }}>{actionError}</p>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title={t("invoices.new")}
        maxWidth={440}
      >
        {bookings.length === 0 ? (
          <p style={{ color: "var(--muted)" }}>{t("invoices.no_bookings")}</p>
        ) : (
          <>
            <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "var(--muted)" }}>
              {t("invoices.select_booking")}
            </label>
            <select
              value={selectedBookingId}
              onChange={(e) => setSelectedBookingId(e.target.value)}
              style={{ width: "100%", marginBottom: "1rem", padding: "0.5rem", borderRadius: "0.375rem", border: "1px solid var(--border)", background: "var(--bg)", color: "var(--text)" }}
            >
              <option value="">— choose —</option>
              {bookings.map((b) => (
                <option key={b.ID} value={b.ID}>
                  {b.Title} ({formatPrice(b.PriceCents, b.Currency)})
                </option>
              ))}
            </select>
          </>
        )}
        {actionError && <p style={{ color: "var(--danger)", marginBottom: "0.75rem", fontSize: "0.875rem" }}>{actionError}</p>}
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
          <button className="btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!selectedBookingId || creating2}
            onClick={handleCreate}
          >
            {creating2 ? "Creating…" : t("invoices.save_draft")}
          </button>
        </div>
      </Modal>

      {invoices.length === 0 ? (
        <p style={{ color: "var(--muted)" }}>{t("invoices.empty")}</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {invoices.map((inv) => (
            <div
              key={inv.ID}
              onClick={() => navigate('/app/invoices/' + inv.ID)}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "0.75rem",
                padding: "1rem 1.25rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                cursor: "pointer",
                transition: "border-color 0.15s",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = "var(--accent)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = "var(--border)"; }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "baseline", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ fontWeight: 600, fontSize: "1rem" }}>{inv.Number}</span>
                  <StatusBadge
                    label={INVOICE_STATUS_LABELS[inv.Status]}
                    color={INVOICE_STATUS_COLORS[inv.Status]}
                    style={{
                      background: INVOICE_STATUS_COLORS[inv.Status] + "22",
                      color: INVOICE_STATUS_COLORS[inv.Status],
                      border: `1px solid ${INVOICE_STATUS_COLORS[inv.Status]}55`,
                    }}
                  />
                  {inv.CreditOf && (
                    <span style={{
                      fontSize: "0.75rem",
                      padding: "0.1rem 0.4rem",
                      borderRadius: 999,
                      background: "color-mix(in srgb, var(--warning) 15%, transparent)",
                      color: "var(--warning)",
                      border: "1px solid color-mix(in srgb, var(--warning) 40%, transparent)",
                      fontWeight: 600,
                    }}>
                      Creditnota
                    </span>
                  )}
                </div>
                <div style={{ fontWeight: 600 }}>{formatPrice(inv.TotalCents, inv.Currency)}</div>
              </div>
              <div style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                {inv.DueDate && <span>Vervalt {new Date(inv.DueDate).toLocaleDateString("nl-NL")}</span>}
                {inv.DueDate && inv.PaidAt && " · "}
                {inv.PaidAt && <span>Betaald {new Date(inv.PaidAt).toLocaleDateString("nl-NL")}</span>}
                {!inv.DueDate && !inv.PaidAt && <span style={{ fontStyle: "italic" }}>Geen vervaldatum</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
